// The frame of the signed-out pages (sign in, choose a password), after Isomorphic's
// auth-wrapper-one as the mocks use it (design/isomorphic/src/pages/sign-in.tsx): an always-dark
// brand panel that draws the product — a host, isolated environments, disposable workers, keys
// that stay on your devices — and the form column with the colour-mode toggle.
import { LockKey, ShieldCheck, TreeStructure } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { ThemeToggle } from "../layout/HeaderActions";
import { useT } from "../i18n";
import { Logo } from "../ui/Logo";

const polar = (r: number, deg: number) => ({
  x: 200 + r * Math.cos((deg * Math.PI) / 180),
  y: 200 + r * Math.sin((deg * Math.PI) / 180),
});

function Orbit() {
  return (
    <svg viewBox="0 0 400 400" className="h-auto w-full max-w-[380px]" aria-hidden>
      <defs>
        <radialGradient id="signin-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgb(var(--primary-default))" stopOpacity="0.55" />
          <stop offset="100%" stopColor="rgb(var(--primary-default))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="200" r="190" fill="url(#signin-glow)" opacity="0.5" />
      <circle cx="200" cy="200" r="168" fill="none" stroke="white" strokeOpacity="0.08" strokeDasharray="2 6" />
      <circle cx="200" cy="200" r="118" fill="none" stroke="white" strokeOpacity="0.12" />
      <circle cx="200" cy="200" r="68" fill="none" stroke="white" strokeOpacity="0.16" />
      {[0, 52, 104, 156, 208, 260, 312].map((a, i) => {
        const { x, y } = polar(168, a);
        return <circle key={a} cx={x} cy={y} r={i % 3 === 0 ? 6 : 4} fill="white" fillOpacity={i % 3 === 0 ? 0.9 : 0.35} />;
      })}
      {[30, 150, 270].map((a) => {
        const { x, y } = polar(118, a);
        return (
          <g key={a}>
            <rect x={x - 17} y={y - 17} width="34" height="34" rx="9" fill="rgb(var(--panel))" stroke="rgb(var(--secondary-default))" strokeOpacity="0.8" />
            <rect x={x - 6} y={y - 6} width="12" height="12" rx="3" fill="rgb(var(--secondary-default))" />
          </g>
        );
      })}
      <circle cx="200" cy="200" r="40" fill="rgb(var(--primary-default))" />
      <path d="M188 198v-7a12 12 0 0 1 24 0v7" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
      <rect x="182" y="197" width="36" height="26" rx="6" fill="white" />
      <circle cx="200" cy="209" r="3.5" fill="rgb(var(--primary-default))" />
    </svg>
  );
}

const FACTS = [
  { Icon: TreeStructure, key: "isolation" },
  { Icon: ShieldCheck, key: "workers" },
  { Icon: LockKey, key: "e2ee" },
] as const;

/** `actions` sit beside the colour-mode toggle (the language switcher). */
export function SignInLayout({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const t = useT().auth.layout;
  return (
    <div className="grid min-h-screen bg-gray-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <aside className="relative hidden overflow-hidden bg-panel p-10 lg:flex lg:flex-col xl:p-14">
        <Logo tone="light" />
        <div className="flex flex-1 items-center justify-center py-8">
          <Orbit />
        </div>
        <h2 className="max-w-md font-display text-[30px] font-extrabold leading-tight !text-white">
          {t.headline} <span className="text-secondary">{t.headlineKeys}</span>
        </h2>
        <ul className="m-0 mt-6 max-w-md list-none space-y-3.5 p-0">
          {FACTS.map(({ Icon, key }) => (
            <li key={key} className="flex gap-3 text-[13.5px] leading-relaxed text-panel-foreground">
              <Icon weight="fill" className="mt-0.5 h-[18px] w-[18px] shrink-0 text-secondary" />
              {t.facts[key]}
            </li>
          ))}
        </ul>
      </aside>

      <main className="relative flex flex-col px-5 py-6 sm:px-10">
        <div className="flex items-center justify-between">
          <span className="lg:invisible">
            <Logo />
          </span>
          <div className="flex items-center gap-2">
            {actions}
            <ThemeToggle />
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center py-10">{children}</div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
          <span>{t.footer}</span>
          <span className="font-mono">{window.location.hostname}</span>
        </div>
      </main>
    </div>
  );
}
